/**
 * Authentication API
 * 
 * Service layer for authentication API calls
 * Integrates with backend PostgreSQL auth endpoints
 * Uses AuthService for comprehensive auth operations
 */

import { AuthService, LoginRequest, LoginResponse, CurrentUserResponse } from './auth-service';
import { User } from '@/store/auth-store';
import { logger } from '@/lib/logger';

// ============================================================================
// LEGACY INTERFACES (for backward compatibility)
// ============================================================================

export interface LoginCredentials {
  emailId: string;
  password: string;
  tenant_code?: string;   // Optional when accessing via tenant subdomain — backend resolves from Host header.
  from_swagger?: number;  // Legacy field, will be removed
  ipInfo?: any;          // Optional IP info for audit logging
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  expires_in: number;
  access_token: string;
}

// ============================================================================
// AUTHENTICATION API
// ============================================================================

export const authApi = {
  /**
   * Login user with email, password, and tenant_code
   *
   * SECURITY: Using HttpOnly cookies - no tokens in response
   * Maps frontend LoginCredentials to backend LoginRequest format
   * Returns data in legacy format for backward compatibility
   */
  login: async (credentials: LoginCredentials): Promise<{ token: string; user: User; access_token: string; expires_in: number; csrf_token?: string }> => {
    // Map to backend format
    const loginRequest: LoginRequest = {
      email: credentials.emailId,
      password: credentials.password,
      tenant_code: credentials.tenant_code
    };

    // Use AuthService for the actual login (sets HttpOnly cookies)
    const response: LoginResponse = await AuthService.login(loginRequest);

    // Validate response structure
    if (!response) {
      logger.error('❌ [AuthAPI] Empty response from login');
      throw new Error('Login failed: Empty response from server');
    }

    if (!response.user) {
      logger.error('❌ [AuthAPI] Response missing user object:', response);
      // Check if response has error info
      // A 200 whose body has no `user`. `detail` here is a raw body field, not
      // an axios error, so extractApiErrorMessage does not apply — but it can
      // still be a 422-style array, which would stringify to "[object Object]".
      // Only a real string is shown.
      const raw = (response as any).detail ?? (response as any).message;
      const errorMsg = typeof raw === 'string' && raw ? raw : 'Login failed: Invalid response from server';
      throw new Error(errorMsg);
    }

    // Map backend response to legacy frontend format
    const legacyUser: User = {
      user_id: response.user.user_id,
      email: response.user.email,
      username: response.user.username,
      tenant_id: response.user.tenant_id,
      display_name: response.user.display_name,
      permissions: response.user.permissions
    };

    return {
      token: response.session_id,  // Return session_id (tokens in HttpOnly cookies)
      user: legacyUser,
      access_token: response.access_token,  // JWT token for WebSocket auth
      expires_in: response.expires_in,
      csrf_token: response.csrf_token  // CSRF token for state-changing requests
    };
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    logger.debug('🚪 [AuthAPI] Logout request');
    await AuthService.logout();
  },

  /**
   * Refresh authentication token
   *
   * SECURITY: Using HttpOnly cookies - tokens refreshed automatically
   * Note: This maintains legacy interface but uses new AuthService
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    logger.debug('🔄 [AuthAPI] Token refresh request');

    // Refresh via HttpOnly cookies (POST, refresh token sent as HttpOnly cookie)
    const response = await AuthService.refreshToken();

    return {
      success: response.success,
      message: response.message,
      expires_in: response.expires_in,
      access_token: response.access_token
    };
  },

  /**
   * Get current user info with permissions
   */
  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    logger.debug('👤 [AuthAPI] Get current user request');
    return await AuthService.getCurrentUser();
  },

  /**
   * Validate current session
   */
  validateSession: async (): Promise<boolean> => {
    logger.debug('🔍 [AuthAPI] Session validation request');
    const result = await AuthService.validateSession();
    return result.isValid;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async (): Promise<boolean> => {
    return await AuthService.isAuthenticated();
  },

  // ============================================================================
  // LEGACY METHODS (deprecated but maintained for compatibility)
  // ============================================================================

  /**
   * @deprecated Use getCurrentUser() instead
   */
  getUserSessions: async (): Promise<any> => {
    logger.warn('⚠️  [AuthAPI] getUserSessions is deprecated, use getCurrentUser instead');
    const userInfo = await AuthService.getCurrentUser();
    return { sessions: [], user: userInfo };
  },

  /**
   * @deprecated Session management should be handled server-side
   */
  endSession: async (infoId: string): Promise<void> => {
    logger.warn('⚠️  [AuthAPI] endSession is deprecated');
    // For now, just logout the user
    await AuthService.logout();
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extract tenant_id from various sources
 * Priority: localStorage > URL params > default
 */
export function getTenantId(): string {
  if (typeof window === 'undefined') return 'default';

  // Try to get from stored user data
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      const tenantId = parsed.state?.user?.tenant_id;
      if (tenantId) return tenantId;
    }
  } catch (error) {
    logger.warn('Failed to get tenant_id from storage:', error);
  }

  // Try to get from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlTenantId = urlParams.get('tenant_id');
  if (urlTenantId) return urlTenantId;

  // Default tenant for development
  return 'default-tenant';
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
